import { formatNumber, formatValueWithUnits } from "../../lmv-compat/measurement/UnitFormatter";
import { fetchJson } from "../../net/fetch";
import { base64EncArr, BBOX_SIZE, FRAG_SIZE, serializeBoundingBox, serializeFragment } from "../utils/FragmentData";
import * as dte from "../DtEventTypes";
import { ELEMENT_CLONE_OVERLAY } from "../facets/FacetVizEffects";
import { IotReadings } from "./IotReading";
import { ColumnFamilies, ElementFlags, QC } from "../schema/dt-schema";
import { DtConstants } from "../schema/DtConstants";
import { StreamUtils } from "./StreamUtils";
import ReadingsByDate, { GRANULARITY } from "./ReadingsByDate";
import { TelemetryView } from "./timeline/TelemetryView";
import StreamEvents from "../worker/streamevents/StreamEvents";
import { matchClassification } from "../schema/matchClassification";

const { AttributeType, ChangeTypes, StreamOfflineTimeouts } = DtConstants;

const CONNECTIONS_CATEGORY = DtConstants.RC['IoT Connections'];
const CONNECTION_UNIFORMAT = 'D7070';

const asFakeAttr = (id, displayName) => ({ displayName: displayName ?? id, hash: `[${id}]`, id });
// Internal and transient fake attributes for the purpose of import/export serialization
const ImportExport = {
  Attr: {
    // Host
    HostElementID: asFakeAttr('HostElementID'),
    HostModelID: asFakeAttr('HostModelID'),
    // Settings
    frequency: asFakeAttr('frequency', 'Frequency'),
    offlineTimeout: asFakeAttr('offlineTimeout', 'Offline Timeout'),
    retentionPeriod: asFakeAttr('retentionPeriod', 'Retention Period')
  },
  /** Returns the column name to use for an attribute in the serialized version. */
  getColName: (attr) => {
    switch (typeof attr) {
      case 'string':
        if (!(attr = DtConstants.StandardAttributes[attr])) {
          console.warn('unable to resolve attribute');
          return null;
        }
      case 'object':
        return attr.displayName;
      default:
        console.warn('invalid attribute data type', attr);
    }
  }
};

// TODO: generalize (beyond position+normal) and move to more appropriate location
function createInterleavedGeom(bufferGeom) {

  const vertices = bufferGeom.attributes.position.array;
  const normals = bufferGeom.attributes.normal.array;
  const indices = bufferGeom.attributes.index.array;

  var geom = new THREE.BufferGeometry();

  var vb = new Float32Array(vertices.length + normals.length);

  geom.vbstride = 6;

  geom.vb = vb;
  geom.ib = indices;

  // fill interleaved vb
  var dst = 0;
  for (var i = 0; i < vertices.length; i += 3) {
    vb[dst++] = vertices[i];
    vb[dst++] = vertices[i + 1];
    vb[dst++] = vertices[i + 2];
    vb[dst++] = normals[i];
    vb[dst++] = normals[i + 1];
    vb[dst++] = normals[i + 2];
  }

  // position attribute
  var attrPos = new THREE.BufferAttribute(undefined, 3);
  attrPos.itemOffset = 0;
  geom.attributes.position = attrPos;

  // normal attribute
  var attrNormal = new THREE.BufferAttribute(undefined, 3);
  attrNormal.itemOffset = 3;
  geom.attributes.normal = attrNormal;

  // index attribute
  var attrIndex = new THREE.BufferAttribute(undefined, 1);
  attrIndex.bytesPerItem = 2;
  geom.attributes.index = attrIndex;

  geom.attributesKeys = Object.keys(geom.attributes);

  return geom;
}

// Ensures that network calls are not duplicated
function deduplicatedFetch(streamIDs, fetchCB, inflightCache) {
  const idsToRequest = streamIDs.filter((id) => !inflightCache.has(id));

  if (idsToRequest.length > 0) {
    // Set batched data retrieval.
    const request = fetchCB(idsToRequest).
    finally(() => idsToRequest.forEach((id) => inflightCache.delete(id)));
    // Update inflight cache
    idsToRequest.forEach((id) => inflightCache.set(id, request));
  }

  return Promise.all(streamIDs.map((id) => inflightCache.get(id)));
}

export class StreamManager {
  constructor(facility) {
    this.facility = facility;
    this.viewer = undefined;
    this.defaultModel = undefined;

    this.inflightCaches = {
      infos: new Map()
    };
    this.streamsCache = new Map();
    this.connectedAttrsCache = new Map();
    this.readingsByDate = new ReadingsByDate(this);
    this.view = new TelemetryView(this);
    this.streamEvents = new StreamEvents(this);
    this.boundOnModelChanged = this.onModelChanged.bind(this);
    this.boundOnModelMetaChanged = this.onModelMetaChanged.bind(this);
    this.boundOnWsReconnect = this.onWsReconnect.bind(this);
    this.iotWorkerCbId = -1;
  }

  get heatmap() {
    console.log("DEPRECATED use of StreamManager.heatmap");
    return this.facility.viewer?.dataViz.heatmap;
  }

  get dataVizToolbarCtrl() {
    console.log("DEPRECATED use of StreamManager.dataVizToolbarCtrl");
    return this.facility.viewer?.dataViz;
  }

  // elementId of the root document (which grants access to the webhook endpoints)
  getRootElementID() {
    return 'AQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA';
  }

  // Actual overlay and markers initialization only happen when a default model is found
  _initImpl() {
    this.streamsCache = new Map();
    this.connectedAttrsCache = new Map();
    this.view.init(this.viewer, this.facility.eventTarget);
    this.facility.eventTarget.addEventListener(dte.DT_MODEL_CHANGED_EVENT, this.boundOnModelChanged);
    this.facility.eventTarget.addEventListener(dte.DT_FACILITY_CHANGED_EVENT, this.boundOnModelChanged);
    this.facility.eventTarget.addEventListener(dte.DT_MSG_WS_RECONNECTED, this.boundOnWsReconnect);
    this.addIotEventListener();
  }

  onModelMetaChanged({ change }) {
    if (change.type === 'create' && change.model.urn() === this.facility.getDefaultModelId()) {
      this._initImpl();
      this.facility.eventTarget.removeEventListener(dte.DT_MODEL_METADATA_CHANGED_EVENT, this.boundOnModelMetaChanged);
    }
  }

  init(viewer) {
    this.viewer = viewer;

    // only run internal initialization if there's a default model
    const model = this.facility.getDefaultModel();
    if (model) {
      this._initImpl();
    } else {
      // defer actual init until a default model is created
      this.facility.eventTarget.addEventListener(dte.DT_MODEL_METADATA_CHANGED_EVENT, this.boundOnModelMetaChanged);
    }
  }

  dispose() {
    this.view.dispose();
    if (this.viewer) {
      // Clean up if overlay initialization happened
      this.facility.facetsManager.facetsEffects.clearStreamsOverlay();
      this.streamsCache.clear();
      this.connectedAttrsCache.clear();
      this.streamEvents.clearStreamEvents();
      this.facility.eventTarget.removeEventListener(dte.DT_MODEL_CHANGED_EVENT, this.boundOnModelChanged);
      this.facility.eventTarget.removeEventListener(dte.DT_FACILITY_CHANGED_EVENT, this.boundOnModelChanged);
      this.facility.eventTarget.removeEventListener(dte.DT_MSG_WS_RECONNECTED, this.boundOnWsReconnect);
      this.removeIotEventListener();
    } else {
      // Remove listener waiting for default model creation
      this.facility.eventTarget.removeEventListener(dte.DT_MODEL_METADATA_CHANGED_EVENT, this.boundOnModelMetaChanged);
    }
    this.viewer = undefined;
  }

  async _getDefaultModel() {
    try {
      this.defaultModel = this.facility.getDefaultModel();

      if (!this.defaultModel) {
        return null;
      }

      await this.defaultModel.waitForLoad(false, true);
      return this.defaultModel;
    } catch (e) {
      console.warn('Unable to wait for default model', e);
      return null;
    }
  }

  async _getOrCreateDefaultModel() {
    await this._getDefaultModel();

    if (!this.defaultModel) {
      await this.facility.createDefaultModel();
      this.defaultModel = this.facility.getDefaultModel();
    }

    return this.defaultModel;
  }

  onHostHighlightChange({ model, hostId, highlight }) {
    if (highlight) {
      this.facility.facetsManager.facetsEffects.addElementHighlight(model, hostId, ELEMENT_CLONE_OVERLAY);
    } else {
      this.facility.facetsManager.facetsEffects.removeElementHighlight(model, hostId, ELEMENT_CLONE_OVERLAY);
    }
  }

  async makeStreamGeometry() {
    if (!this.streamGeometryPromise) {
      this.streamGeometryPromise = (async () => {
        const streamGeom = createInterleavedGeom(new THREE.SphereBufferGeometry(0.5, 32, 24));
        const res = await this.defaultModel.createGeometry(streamGeom);
        return res;
      })();
    }
    return this.streamGeometryPromise;
  }

  async addStreamFragmentFromParent(element, model, dbId) {
    const box = model.getElementBounds(dbId);
    if (box.isEmpty()) {
      console.error('Failed to obtain bounding box of parent geometry');
      return;
    }
    const center = box.getCenter();
    center.add(model.getGlobalOffset());


    const { hash } = await this.makeStreamGeometry();
    // TODO: here we are ignoring the transform, as the geometry is the unit sphere.
    let m = new THREE.Matrix4();
    m.setPosition(center);
    const fragmentData = serializeFragment(0, hash, null, m.toArray());
    const lmvFragment = base64EncArr(fragmentData, 0, FRAG_SIZE);

    const bounds = new THREE.Box3();
    bounds.setFromCenterAndSize(center, new THREE.Vector3(1, 1, 1));
    const boundingBoxData = serializeBoundingBox(bounds);
    const lmvBBox = base64EncArr(boundingBoxData, 0, BBOX_SIZE);

    element[DtConstants.QC.LmvFragment] = lmvFragment;
    element[DtConstants.QC.LmvBoundingBox] = lmvBBox;
  }

  // create a logical stream element
  async createStream(name, modelUrnOfParent, dbIdOfParent, extraProps = {}) {

    const props = { [QC.Name]: name, ...extraProps };
    const host = modelUrnOfParent && dbIdOfParent ? [modelUrnOfParent, dbIdOfParent] : null;

    const { newIds: [newElmKey] } = await this.createBaseConnections([props], [host]);

    // set stream ingestion password
    await this.resetStreamSecrets([newElmKey]);

    return newElmKey;
  }

  /** Create a logical calculated stream element and link it to some physical asset
   * @param {string} name
   * @param {string} modelUrnOfParent
   * @param {string} dbIdOfParent
   * @param {string} tandemCategory - Tandem category of the stream
   * @param {Object} extraProps - Extra properties for the stream
   * @param {number} [extraProps.frequency] - Frequency of the stream (capacity-aware accounts only)
   * @param {number} [extraProps.retentionPeriod] - Retention period of the stream (capacity-aware accounts only)
   * @returns {string} The database ID of the created stream
   */
  async createCalculatedStream(name, modelUrnOfParent, dbIdOfParent, tandemCategory = DtConstants.TC.Sensors, extraProps = {}) {
    const { frequency, retentionPeriod, ...restProps } = extraProps;

    const settings = {
      thresholds: {},
      offlineTimeout: StreamOfflineTimeouts.Never,
      calculationSettings: {}
    };
    const account = this.facility.getAccount();
    if (account?.isCapacityBased()) {
      settings.frequency = frequency;
      settings.retentionPeriod = retentionPeriod;
    }
    const props = {
      [QC.Name]: name,
      [QC.CategoryId]: CONNECTIONS_CATEGORY,
      [QC.ElementFlags]: ElementFlags.Stream,
      [QC.TandemCategory]: tandemCategory,
      [QC.Settings]: settings,
      ...restProps
    };
    const host = modelUrnOfParent && dbIdOfParent ? [modelUrnOfParent, dbIdOfParent] : null;

    const { newDbIds: [dbId] } = await this.createBaseConnections([props], [host]);

    return dbId;
  }

  // Can be used to create possibly capacity consuming connections in bulk
  async createBaseConnections(propsList, hostsList) {
    if (!this.defaultModel) {
      await this._getOrCreateDefaultModel();
    }

    const settingsList = [];
    await Promise.all(propsList.map((props, i) => {
      // Add connection defaults.
      this.addDefaultsToCreateMutations(props);

      // Ensure hosts can be linked.
      let hostPromise = hostsList[i] && this.defaultModel.addParentXRef(props, [hostsList[i]]);

      // Split creation and post-processing of settings.
      settingsList.push(props[QC.Settings]);
      // it is important we send a non-empty settings object to ensure the server doesn't apply defaults.
      props[QC.Settings] = {};

      return hostPromise;
    }));

    // First, we create the base connections without settings.
    const createdElements = await this.defaultModel.createElementsWithData(propsList, DtConstants.ChangeDesc.CreateStreams);

    // Then, update base connection with potentially capacity-consuming settings.
    try {
      await this.#updateStreamsSettingsBulk(createdElements.newDbIds, settingsList, DtConstants.ChangeDesc.UpdateStreamsConfig);
    } catch (e) {
      // TODO disambiguate failure modes (Capacity vs Server-Error)
      console.warn('failure to update stream settings, rolling back.');
      await this.defaultModel.deleteElements(createdElements.newDbIds, DtConstants.ChangeDesc.DeleteStreams);
      throw e;
    }

    return createdElements;
  }

  getImportExportAttributes() {
    const { StandardAttributes, QC } = DtConstants;

    const attributes = [
    StandardAttributes[QC.Name],
    StandardAttributes[this.facility.getSchemaVersion() > 1 ? QC.TandemCategory : QC.UniformatClass],
    StandardAttributes[QC.Classification],
    ImportExport.Attr.HostElementID,
    ImportExport.Attr.HostModelID,
    ImportExport.Attr.offlineTimeout];


    const account = this.facility.getAccount();
    if (account?.isCapacityBased()) {
      attributes.push(ImportExport.Attr.frequency);
      attributes.push(ImportExport.Attr.retentionPeriod);
    }

    attributes.asHeaderDict = () => attributes.reduce((dict, attr) => {
      dict[ImportExport.getColName(attr)] = attr.hash;
      return dict;
    }, {});

    return attributes;
  }

  //  defines which columns will be available in the template - currently we base it of our default parameter set applied to every created stream + standard override property for name
  getStreamsBulkImportTemplate() {
    const attrs = this.getImportExportAttributes();

    function createMockRow(muts) {
      const row = {};
      for (const attr of attrs) {
        switch (attr.id) {
          case ImportExport.Attr.frequency.id:
          case ImportExport.Attr.retentionPeriod.id:
          case ImportExport.Attr.offlineTimeout.id:
            row[ImportExport.getColName(attr)] = muts[QC.Settings]?.[attr.id];
            break;
          default:
            row[ImportExport.getColName(attr)] = muts[attr.id];
        }
      }
      return row;
    }

    return [
    attrs.asHeaderDict(),
    createMockRow(this.addDefaultsToCreateMutations({ [QC.Name]: 'Stream 1' })),
    createMockRow(this.addDefaultsToCreateMutations({ [QC.Name]: 'Stream 2' }))];

  }

  /**
   * Import streams from JSON data.
   * Supports updates to streams by providing the fully-qualified element ID and facility ID. Ignores unchanged streams.
   * Ignores empty values in the CSV, unsetting values is not supported
   * @param {Object[]} data
   * @returns {string[]} created/modified stream ids
   */
  async importConnectionsFromJson(data) {
    const schema = this.getImportExportAttributes().asHeaderDict();
    return this.importStreamsFromJsonTemplate(schema, data);
  }

  /**
   * Import streams from JSON data.
   * Supports updates to streams by providing the fully-qualified element ID and facility ID. Ignores unchanged streams.
   * Ignores empty values in the CSV, unsetting values is not supported
   * @param {Object} schema Header row when non-standard.
   * @param {Object[]} data
   * @returns {string[]} created/modified stream ids
   */
  async importStreamsFromJsonTemplate(schema, data) {
    const streams = await this.getAllStreamInfos();
    const existingStreams = new Map(streams.map((s) => [s.fullId, s]));

    const defaultModel = await this._getOrCreateDefaultModel();
    const dbId2catId = defaultModel.getData().dbId2catId;

    const parents = {};


    for (let props of data) {
      if (!props.fullId) {
        continue;
      }
      const stream = existingStreams.get(props.fullId);

      if (!stream?.hostElement) {
        continue;
      }

      const [elementId] = await stream.hostElement.model.getElementIdsFromDbIds([stream.hostElement.hostId]);
      parents[stream.fullId] = { modelId: stream.hostElement.model.urn(), elementId };
    }

    const updatedStreams = [];
    const newStreams = [];

    for (let streamProps of data) {
      const res = {
        [QC.ElementFlags]: ElementFlags.Stream
      };

      const existingStream = existingStreams.get(streamProps?.fullId);

      // Compare with existing streams in the facility and filter out incorrect and unchanged
      if (streamProps?.fullId?.length) {
        if (!existingStream) {
          console.error(`Unknkown full id for connection "${streamProps.Name}", skipping...`);
          continue;
        }

        // Check that it is coming from the right facility
        if (!streamProps.facility?.length) {
          console.error(`Facility ID is missing for "${streamProps.Name}", skipping...`);
          continue;
        }

        if (streamProps.facility !== this.facility.urn()) {
          console.error(`Connection "${streamProps.Name}" seems to be coming from another facility. ${streamProps.facility} instead of ${this.facility.urn()}, skipping...`);
          continue;
        }

        // Fill in missing categoryId for older streams
        if (dbId2catId[existingStream.dbId] !== CONNECTIONS_CATEGORY) {
          res[QC.CategoryId] = CONNECTIONS_CATEGORY;
        }
        res[QC.LmvDbId] = existingStream.dbId;
        // added to existing stream only if we see any update.
      } else {
        // Add default values for new connections
        this.addDefaultsToCreateMutations(res);
        newStreams.push(res);
      }

      let updated = false;
      const updateLink = {};
      const attrs = this.getImportExportAttributes();
      for (let attr of attrs) {
        const prop = ImportExport.getColName(attr);
        if (!streamProps[prop]) {
          // property is not set for the stream
          continue;
        }

        switch (attr.id) {
          case QC.Name:
            if (!existingStream) {
              res[QC.Name] = streamProps[prop];
            } else if (existingStream.name !== streamProps[prop]) {
              res[QC.OName] = streamProps[prop];
              updated = true;
            }
            continue;

          case QC.UniformatClass:
            if (!existingStream) {
              res[QC.UniformatClass] = streamProps[prop];
            } else if (existingStream.dtClass !== streamProps[prop]) {
              res[QC.OUniformatClass] = streamProps[prop];
              updated = true;
            }
            continue;

          case QC.TandemCategory:
            if (!existingStream) {
              res[QC.TandemCategory] = streamProps[prop];
            } else if (existingStream.tandemCategory !== streamProps[prop]) {
              res[QC.OTandemCategory] = streamProps[prop];
              updated = true;
            }
            continue;

          case QC.Classification:
            if (!existingStream) {
              res[QC.Classification] = streamProps[prop];
            } else if (existingStream.userClassification !== streamProps[prop]) {
              res[QC.OClassification] = streamProps[prop];
              updated = true;
            }
            continue;

          case ImportExport.Attr.HostElementID.id:
          case ImportExport.Attr.HostModelID.id:
            updateLink[prop] = streamProps[prop];
            // setting updated in further processing
            continue;

          case ImportExport.Attr.frequency.id:
          case ImportExport.Attr.retentionPeriod.id:
          case ImportExport.Attr.offlineTimeout.id:
            const numValue = Number(streamProps[prop]);
            if (!Number.isInteger(numValue) || numValue < 0) {
              console.warn(`Invalid value ${streamProps[prop]} for ${prop}, skipping...`);
              continue;
            }
            if (!existingStream || existingStream.settings?.[attr.id] !== numValue) {
              res[QC.Settings] ??= {};
              res[QC.Settings][attr.id] = numValue;
              updated = true;
            }
            continue;

          default:
            console.warn('unrecognized import/export attribute', attr);
        }
      }

      if (updateLink.HostElementID && updateLink.HostModelID) {
        if (
        parents[streamProps.fullId]?.modelId !== updateLink.HostModelID ||
        parents[streamProps.fullId]?.elementId !== updateLink.HostElementID)
        {
          // we know the update contains a new link, can convert it to element properties now
          const targetModel = this.facility.getModelByUrn(updateLink.HostModelID);
          if (!targetModel) {
            console.error(`Failed to find target model ${updateLink.HostModelID}, skipping the link creation`);
            continue;
          }
          const [targetDbId] = await targetModel.getDbIdsFromElementIds([updateLink.HostElementID]);

          if (!targetDbId) {
            console.error(`Failed to find target dbId for ${updateLink.HostElementID}, skipping the link creation`);
            continue;
          }
          await defaultModel.addParentXRef(res, [[updateLink.HostModelID, targetDbId]]);
          updated = true;
        }
      }

      if (existingStream && updated) {
        // only update connections that have changes.
        updatedStreams.push([existingStream, res]);
      }
    }

    // No changes detected
    if (!updatedStreams.length && !newStreams.length) {
      console.log('Import finished without changes to connections');
      return [];
    }

    if (!this.defaultModel) {
      await this._getOrCreateDefaultModel();
    }

    let newKeys = [];
    let promises = [];

    // Newly created connections
    if (newStreams.length) {
      promises.push((async () => {
        ({ newIds: newKeys } = await this.createBaseConnections(newStreams, [] /* In props already */));
        await this.resetStreamSecrets(newKeys);
      })());
    }

    // Updating existing connections
    if (updatedStreams.length) {
      promises.push((async () => {
        const dbIds = [];
        const settingsMutations = [];
        const createMutations = [];
        for (const [stream, props] of updatedStreams) {
          const { [QC.Settings]: settings, ...restProps } = props;
          createMutations.push(restProps);
          if (settings) {
            dbIds.push(stream.dbId);
            settingsMutations.push({ ...stream.settings, ...settings });
          }
        }

        // Split creation and post-processing of settings.
        await this.defaultModel.updateElementsWithData(createMutations, DtConstants.ChangeDesc.UpdateStreams);
        await this.#updateStreamsSettingsBulk(dbIds, settingsMutations, DtConstants.ChangeDesc.UpdateStreamsConfig);
      })());
    }

    await Promise.all(promises);
    console.log(`Connections import result: ${newKeys.length} new, ${updatedStreams.length} modified, ${data.length - updatedStreams.length - newKeys.length} skipped connections.`);

    return newKeys;
  }

  /**
   * Creates table-like JSON object from given streams
   * @param {Stream[]} [streams] if provided will export only given streams
   * @returns {Promise<*[]>}
   */
  async exportStreamsToJson(streams = []) {
    const header = this.getImportExportAttributes().asHeaderDict();
    if (!streams?.length) {
      console.warn('exporting data for 0 connection');
      return [header];
    }

    const keys = streams.map((s) => s.fullId);

    const defaultModel = this.facility.getDefaultModel();
    const secrets = await fetchJson(this.facility.loadContext, `/models/${defaultModel.urn()}/getstreamssecrets`, "POST", { keys, includeEmpty: true });

    const res = await Promise.all(streams.map(async (s) => {
      const { name, fullId, streamUrlRaw, dtClass, settings, tandemCategory, userClassification } = s;
      const ingestionSecret = secrets[fullId];
      if (!ingestionSecret) {
        console.error(`secret is missing for stream ${fullId}`);
      }

      const ingestionUrl = this._makeIngestionUrl(streamUrlRaw, ingestionSecret || 'MISSING_SECRET');
      let elmId;

      if (s.hostElement) {
        [elmId] = await s?.hostElement.model.getElementIdsFromDbIds([s.hostElement.hostId]);
      }
      //  this is the list of data that is actually exported
      const json = {
        [ImportExport.getColName(QC.Name)]: name,
        [ImportExport.getColName(QC.Classification)]: userClassification,
        facility: this.facility.urn(),
        fullId,
        ingestionUrl,
        [ImportExport.getColName(ImportExport.Attr.HostElementID)]: elmId,
        [ImportExport.getColName(ImportExport.Attr.HostModelID)]: s.hostElement?.model.urn(),
        [ImportExport.getColName(ImportExport.Attr.offlineTimeout)]: settings?.offlineTimeout
      };

      if (this.facility.getSchemaVersion() > 1) {
        json[ImportExport.getColName(QC.TandemCategory)] = tandemCategory;
      } else {
        json[ImportExport.getColName(QC.UniformatClass)] = dtClass;
      }

      const account = this.facility.getAccount();
      if (account?.isCapacityBased()) {
        json[ImportExport.getColName(ImportExport.Attr.frequency)] = settings?.frequency;
        json[ImportExport.getColName(ImportExport.Attr.retentionPeriod)] = settings?.retentionPeriod;
      }

      return json;
    }));


    return [header, ...res];
  }

  /** Default set of mutations to create a connection. */
  addDefaultsToCreateMutations(muts) {
    muts[QC.CategoryId] ??= CONNECTIONS_CATEGORY;
    muts[QC.ElementFlags] ??= ElementFlags.Stream;

    if (this.facility.getSchemaVersion() > 1) {
      muts[QC.TandemCategory] ??= DtConstants.TC.Sensors;
    } else {
      muts[QC.UniformatClass] ??= CONNECTION_UNIFORMAT;
    }

    muts[QC.Settings] ??= {};
    muts[QC.Settings].offlineTimeout ??= StreamOfflineTimeouts.Default;

    const defaults = this.facility.getAccount().getCapacityDefaults();
    muts[QC.Settings].retentionPeriod ??= defaults.retentionPeriod;
    muts[QC.Settings].frequency ??= defaults.frequency;

    return muts;
  }

  async updateLink(streams, hosts) {
    if (streams.length !== hosts.length) {
      console.error("Streams/Targets length mismatch");
      return;
    }

    if (streams.length < 1) {
      return; // no change
    }

    const defaultModel = this.facility.getDefaultModel();
    const dbIds = streams.map((stream) => stream.dbId);
    const hostsPerElement = hosts.map((host) => host ? [host] : []);

    return await defaultModel.updateHosts(dbIds, hostsPerElement);
  }

  validateThresholds(thresholds) {
    if (!thresholds) return true; // effectively removes thresholds

    if (thresholds.alertDefinition && Object.keys(thresholds.alertDefinition).length > 0) {
      const { evaluationPeriodSec } = thresholds.alertDefinition;
      if (typeof evaluationPeriodSec !== 'number' || evaluationPeriodSec <= 0) return false;
    }

    const { lower, upper } = thresholds;
    const numbers = [lower?.alert, lower?.warn, upper?.warn, upper?.alert].filter(isFinite);

    if (numbers.length === 1) return true; // single threshold is always valid

    for (let n = 1; n < numbers.length; n++) {
      // Must be an array of growing numbers
      if (numbers[n - 1] >= numbers[n]) return false;
    }

    return true;
  }

  /**
   * Apply and remove thresholds for the given streams and parameter
   * @param {Streams[]} streams
   * @param {string} attrID
   * @param {{ name: string, lower: { alert: string, warn: string }, upper: { alert: string, warn: string }, alertDefinition: { evaluationPeriodSec: number }}} [newThresholds] omit for removing thresholds
   */
  async updateBulkThresholds(streams, attrID, newThresholds) {
    if (!streams?.length) {
      return; // no change
    }

    if (!this.validateThresholds(newThresholds)) {
      throw new Error(`Invalid threshold value(s) for attribute: ${attrID}`);
    }

    if (!streams.every((stream) => stream.streamAttrs.some((attr) => attr.id === attrID))) {
      throw new Error(`Attribute ${attrID} is not applied to some of the streams`);
    }

    const dbIds = [];
    const settings = [];
    for (const stream of streams) {
      // Rebuild old thresholds because we want to keep streamAttrs.allowedValues.thresholds schema
      const thresholdsByAttrID = stream.streamAttrs.reduce((th, attr) => {
        th[attr.id] = attr.allowedValues?.thresholds;
        return th;
      }, {});

      thresholdsByAttrID[attrID] = newThresholds;

      let storedThresholds;
      for (const aID in thresholdsByAttrID) {
        // Skip undefined thresholds when building BLOB
        if (thresholdsByAttrID[aID]) {
          storedThresholds ??= {};
          storedThresholds[aID] = thresholdsByAttrID[aID];
        }
      }

      const streamSettings = structuredClone(stream.settings || {});

      if (storedThresholds) {
        streamSettings.thresholds = storedThresholds;
      } else {
        delete streamSettings.thresholds;
      }

      dbIds.push(stream.dbId);
      settings.push(streamSettings);
    }

    // Update cached value
    for (const stream of streams) {
      const attr = stream.streamAttrs.find((attr) => attr.id === attrID);
      attr.allowedValues ??= {};
      attr.allowedValues.thresholds = newThresholds;
    }

    await this.#updateStreamsSettingsBulk(dbIds, settings, DtConstants.ChangeDesc.UpdateStreamsThresholds);
  }

  /**
   * @typedef {{ name: string, expr: string, clientData: StateClientData }} StateSetting
   * @typedef {{ conditionSettings?: ConditionSettings, scheduledSettings?: ScheduledSettings }} StateClientData
   * @typedef {{ condition: StateCondition }} ConditionSettings
   * @typedef {{ id: string, combinator: 'and'|'or', rules: StateConditionRule[] }} StateCondition
   * @typedef {{ id: string, field: string, operator: string, value: string | string[] }} StateConditionRule
   * @typedef {{ scheduleMode: 'allHours'|'custom', schedule: Object<number, ScheduledDay> }} ScheduledSettings
   * @typedef {{ enabled: boolean, ranges: ScheduledDayRange[] }} ScheduledDay
   * @typedef {{ start: number, end: number }} ScheduledDayRange
   * @typedef {StateSetting & { state: string, attribute: Attr, connections: Connection[], id: string }} StateItem
   */

  /**
   * Apply and/or remove a state setting for a specific stream attribute across connections.
   * @param {Object} params
   * @param {string} params.attrID stream attribute ID
   * @param {string} params.state e.g., 'available', 'active', 'scheduled'
   * @param {StateSetting} [params.stateSetting] state setting to apply for connections in applyTo
   * @param {Connection[]} [params.applyTo=[]] connections to apply the state setting to
   * @param {Connection[]} [params.removeFrom=[]] connections to remove the state setting from
   */
  async updateBulkStateSettings({ attrID, state, stateSetting, applyTo = [], removeFrom = [] }) {
    if (!applyTo.length && !removeFrom.length) {
      return;
    }
    if (applyTo.length && !stateSetting) {
      throw new Error("stateSetting is required when applyTo is provided");
    }
    const applyToIds = new Set(applyTo.map((connection) => connection.dbId));
    if (removeFrom.some((connection) => applyToIds.has(connection.dbId))) {
      throw new Error("applyTo and removeFrom contain the same connection");
    }

    const applyStateSetting = (connection) => {
      const connectionSettings = structuredClone(connection.settings ?? {});
      const stateSettings = connectionSettings.stateSettings || {};
      stateSettings[attrID] ||= {};
      stateSettings[attrID][state] = stateSetting;
      connectionSettings.stateSettings = stateSettings;
      return connectionSettings;
    };

    const removeStateSetting = (connection) => {
      const connectionSettings = structuredClone(connection.settings ?? {});
      const stateSettings = connectionSettings.stateSettings;
      if (!stateSettings?.[attrID]) {
        return connectionSettings;
      }
      delete stateSettings[attrID][state];
      if (Object.keys(stateSettings[attrID]).length === 0) {
        delete stateSettings[attrID];
      }
      if (Object.keys(stateSettings).length === 0) {
        delete connectionSettings.stateSettings;
      }
      return connectionSettings;
    };

    const dbIds = [];
    const settings = [];

    for (const connection of applyTo) {
      dbIds.push(connection.dbId);
      settings.push(applyStateSetting(connection));
    }
    for (const connection of removeFrom) {
      dbIds.push(connection.dbId);
      settings.push(removeStateSetting(connection));
    }

    await this.#updateStreamsSettingsBulk(dbIds, settings, DtConstants.ChangeDesc.UpdateStreamsStateSettings);
  }

  /**
   * @returns {Promise<StateItem[]>}
   * Compiled list of state settings, augmented with target connections, attribute, and id
   */
  async getStates() {
    const connections = await this.getAllStreamInfos();

    const stateIdToState = new Map();

    for (const connection of connections) {
      const attrIdToAttr = {};
      for (const attr of connection.streamAttrs) {
        attrIdToAttr[attr.id] = attr;
      }

      for (const [attrId, states] of Object.entries(connection.settings?.stateSettings || {})) {
        const attr = attrIdToAttr[attrId];
        if (!attr) {
          continue;
        }
        for (const [state, stateSetting] of Object.entries(states)) {
          const stateId = `${attrId}-${state}-${stateSetting.name}`;
          if (stateIdToState.has(stateId)) {
            stateIdToState.get(stateId).connections.push(connection);
          } else {
            stateIdToState.set(stateId, {
              ...stateSetting,
              state,
              attribute: attr,
              connections: [connection],
              id: stateId
            });
          }
        }
      }
    }

    return Array.from(stateIdToState.values());
  }

  /**
   * Update calculation settings for a specific stream and attribute
   * @param {number} streamDbId - Stream database ID
   * @param {string} attrID - Attribute ID to update calculation settings for
   * @param {{ expr: string, vars: Object<string, {sourceIdentifiers: Array<{elem: string, attr: string}>}>, freq: number, enabled: boolean }} [newCalculationSettings] - New calculation settings, omit to remove
   */
  async updateCalculationSetting(streamDbId, attrID, newCalculationSettings) {
    const [streamId] = await this.defaultModel.getElementIdsFromDbIds([streamDbId]);
    if (!streamId || !attrID) {
      throw new Error("streamId and attrID are required");
    }

    const [stream] = await this.getAllStreamInfos(false, [streamDbId]);
    if (!stream?.settings.calculationSettings) {
      throw new Error('calculationSettings are not present on the connection');
    }

    if (newCalculationSettings) {
      stream.settings.calculationSettings[attrID] = newCalculationSettings;
    } else {
      delete stream.settings.calculationSettings[attrID];
    }

    await this.#updateStreamsSettingsBulk([streamDbId], [stream.settings], DtConstants.ChangeDesc.UpdateStreamsCalculationSettings);
  }

  /**
   * Trigger recompute of a calculated stream for a given number of days
   * @param {string} streamFullId - Full element ID of the stream (connection)
   * @param {string} attrId - Attribute ID to recompute
   * @param {number} calcDays - Number of days to recompute
   */
  async recomputeCalculatedStream(streamFullId, attrId, calcDays) {
    const DAY_IN_MS = 24 * 60 * 60 * 1000;
    const now = Date.now();
    const rem = now % DAY_IN_MS;
    const toTime = rem === 0 ? now : now - rem + DAY_IN_MS;
    const fromTime = toTime - calcDays * DAY_IN_MS;
    const defaultModel = this.facility.getDefaultModel();
    const webAttrId = encodeURIComponent(attrId);
    const url = `/timeseries/models/${defaultModel.urn()}/stream-calculations/${streamFullId}/attributes/${webAttrId}`;
    return fetchJson(this.facility.loadContext, url, 'POST', { fromTime, toTime });
  }

  async #updateStreamsSettingsBulk(dbIds, settings, changeDesc, skipSourceMappingDiff = false) {
    const defaultModel = this.facility.getDefaultModel();

    if (!skipSourceMappingDiff) {
      const diff = await this._diffSourceMapping(dbIds, settings);
      if (diff?.dbIds.length) {
        // has source mapping changes, merge it with incoming settings
        for (let i = 0; i < diff.dbIds.length; i++) {
          const idx = dbIds.indexOf(diff.dbIds[i]);
          if (idx !== -1) {
            settings[idx] = { ...settings[idx], sourceMapping: diff.settings[i].sourceMapping };
          }
        }
      }
    }

    await defaultModel.updateStreamsSettings(dbIds, settings, changeDesc);
    // reload the cache on the main thread
    await this.getAllStreamInfos(true);
    this.facility.eventTarget.dispatchEvent({ type: dte.DT_STREAMS_INFO_CHANGED_EVENT, dbIds });
  }

  async #buildTemplatedSourceMapping() {
    const template = await this.facility.settings.template.get();
    const templateParameters = template?.parameters ?? [];

    const attrs = await this.facility.getDefaultModel().getAttributes({ native: true });
    const attrsByUUID = new Map(attrs.map((attr) => [attr.uuid, attr]));

    const templatedSourceMapping = new Map();
    for (const param of templateParameters) {
      const attr = attrsByUUID.get(param.uuid);

      if (!attr) {
        console.warn(`Attribute for parameter ${param.uuid} not found in the model`);
        continue;
      }

      if (param.sourceAttribute && param.sourceAttribute.startsWith('{')) {
        const mapping = JSON.parse(param.sourceAttribute);
        // indicates that this mapping is coming from the facility template
        mapping.isShared = true;
        templatedSourceMapping.set(attr.id, mapping);
      } else {
        // just track the parameter so that we can check if a parameter is gone from the template
        templatedSourceMapping.set(attr.id, undefined);
      }
    }

    return templatedSourceMapping;
  }

  /**
   * Compare settings with current facility template parameters and stream attributes
   * @returns {Promise<{ settings: Object[], dbIds: number[] }>} changed settings and corresponding stream dbIds
   */
  async _diffSourceMapping(dbIds, settings) {
    // only contain those that have actually changed
    const filteredSettings = [];
    const filteredDbIds = [];
    const streams = await this.getAllStreamInfos(false, dbIds);

    if (streams.length !== dbIds.length) {
      console.error(`Streams length mismatch: ${streams.length} !== ${dbIds.length}`);
      return;
    }

    const templatedSourceMapping = await this.#buildTemplatedSourceMapping();

    for (let i = 0; i < settings.length; i++) {
      let s = settings[i];
      const stream = streams[i];
      let hasChanges = false;

      // helper to ensure we don't mutate input settings and avoids cloning entire settings object
      const ensureMutable = () => {
        if (!hasChanges) {
          s = { ...s, sourceMapping: s.sourceMapping ? { ...s.sourceMapping } : {} };
          hasChanges = true;
        }
      };

      // check for added or updated mappings
      for (const attr of stream.streamAttrs) {
        const attrId = attr.id;
        if (!templatedSourceMapping.get(attrId)) {
          // the templated mapping is not applicable to the stream
          continue;
        }

        if (!s.sourceMapping?.[attrId] || s.sourceMapping?.[attrId]?.isShared) {
          // incoming settings do not have an override, or based on facility template, use the templated mapping
          const currentMapping = s.sourceMapping?.[attrId] ?? null;
          const templateMapping = templatedSourceMapping.get(attrId);
          const mappingChanged = !StreamUtils.areSourceMappingsEqual(currentMapping, templateMapping);

          if (mappingChanged) {
            ensureMutable();
            s.sourceMapping[attrId] = templateMapping;
          }
          continue;
        }
      }

      // check for removed mappings
      if (s.sourceMapping) {
        for (const attrId in s.sourceMapping) {
          const shouldRemove =
          // parameter is gone from the template
          !templatedSourceMapping.has(attrId) ||
          // templated mapping is gone, but a parameter is still present
          !templatedSourceMapping.get(attrId) && s.sourceMapping[attrId]?.isShared ||
          // attribute is gone from the stream - e.g. due to reclassification of the stream
          !stream.streamAttrs.some((a) => a.id === attrId);

          if (shouldRemove) {
            ensureMutable();
            delete s.sourceMapping[attrId];
          }
        }
      }

      if (hasChanges) {
        filteredSettings.push(s);
        filteredDbIds.push(stream.dbId);
      }
    }

    return { settings: filteredSettings, dbIds: filteredDbIds };
  }

  async refreshStreamConfigs(reason) {
    const res = await this.needsStreamConfigSync(true);

    if (!res?.dbIds.length) return;

    await this.#updateStreamsSettingsBulk(res.dbIds, res.settings, reason, true);
  }

  /** @returns {Promise<{ dbIds: number[], settings: Object[] }>} dbIds of streams that need config sync */
  async needsStreamConfigSync(refreshCache = false) {
    const streams = await this.getAllStreamInfos(refreshCache);
    if (streams.length === 0) {
      return { dbIds: [], settings: [] };
    }
    const dbIds = [];
    const settings = [];
    for (const stream of streams) {
      dbIds.push(stream.dbId);
      settings.push(stream.settings ?? {});
    }
    return this._diffSourceMapping(dbIds, settings);
  }

  /**
   * @returns {Promise<{ name: string, lower: { alert: string, warn: string }, upper: { alert: string, warn: string }, alertDefinition: { evaluationPeriodSec: number }, streams: Stream[], attribute: Attr}[]>} - Compiled list of thresholds, augmented with streams and the attributes they target
   */
  async getThresholds() {
    const thresholds = new Map();

    const streams = await this.getAllStreamInfos();

    for (let stream of streams) {
      for (let attr of stream.streamAttrs) {
        if (attr.allowedValues?.thresholds) {
          const threshold = attr.allowedValues.thresholds;
          const alertDefinition = threshold.alertDefinition ?? { evaluationPeriodSec: DtConstants.StreamAlertEvaluationPeriod };

          if (thresholds.has(threshold.name)) {

            if (thresholds.get(threshold.name).attribute.id === attr.id) {
              thresholds.get(threshold.name).streams.push(stream);
            } else {
              console.warn(`Wrong threshold-attribute assignment. Threshold: ${threshold.name} / Stream ID: ${stream.fullId} / Attribute: ${attr.id}`);
            }

            continue;
          }

          thresholds.set(threshold.name, { ...threshold, attribute: attr, streams: [stream], alertDefinition });
        }
      }
    }

    return Array.from(thresholds.values());
  }

  /**
   * Data Points for the given stream, can be from cache.
   *
   * As this function is often called simultaneously on an empty cache, it takes extra-precaution to ensure network
   * requests are not unnecessarily duplicated.
   * @param {number[]} dbIds Stream IDs
   * @param {boolean} wantAlerts if threshold states should be computed
   * @returns {Promise<*[]>}
   */
  async getLastReadings(dbIds = [], wantAlerts = false) {
    if (dbIds.length === 0) {
      return [];
    }

    const [lastByDbId, alerts] = await this.getLastReadingsWithAlerts({ dbIds });
    // Ensures stream cache is populated.
    if (wantAlerts) await this.getAllStreamInfos(false, dbIds);

    const formattedResults = [];
    for (let streamIdx = 0; streamIdx < dbIds.length; streamIdx++) {
      const dbId = dbIds[streamIdx];
      const readingsByAttrID = lastByDbId.get(dbId);
      if (wantAlerts && !readingsByAttrID) {
        formattedResults[streamIdx] = { state: DtConstants.StreamStates.NoData };
        continue;
      }

      let stream = this.streamsCache.get(dbId);

      // make a copy here to avoid modifying the cache entry.
      const enrichedData = {};
      for (let attrID in readingsByAttrID) {
        const reading = readingsByAttrID[attrID];
        let val, ts;
        for (const time in reading) {
          val = reading[time];
          ts = time;
          break;
        }
        const newEntry = { ts: Number(ts), val };

        // If the cached stream entry is missing (due to some race), we still provide the consumer with base data.
        if (wantAlerts && stream) {
          newEntry.states = StreamUtils.getReadingStates(stream, val, ts, alerts.get(dbId)?.[attrID]);
          newEntry.state = newEntry.states[0];
        }

        enrichedData[attrID] = newEntry;
      }
      formattedResults[streamIdx] = enrichedData;
    }

    return formattedResults;
  }

  /** Time of the latest reading. */
  async getLatestReading() {
    // Waits for the instance tree to be loaded, since this is called early.
    const model = await this._getDefaultModel();
    return model?.asyncPropertyOperation({
      "operation": 'GET_LAST_READINGS',
      filter: { onlyLatest: true }
    });
  }

  async getLastReadingsWithAlerts(filter, forceRefresh) {
    return Promise.all([
    this.getLastReadingsFromWorker(filter, forceRefresh),
    this.getStreamAlertsFromWorker(forceRefresh)]
    );
  }

  async refreshStreamsLastReadingsAndAlerts() {
    return Promise.all([
    this.refreshStreamsLastReadings(),
    this.refreshStreamsAlerts()]
    );
  }

  async getStreamAlertsFromWorker(forceRefresh) {
    const model = this.facility.getDefaultModel();
    return model.asyncPropertyOperation({
      "operation": 'GET_STREAM_ALERTS',
      ...(forceRefresh && { forceRefresh })
    });
  }

  async refreshStreamsAlerts() {
    return this.getStreamAlertsFromWorker(true);
  }

  async getLastReadingsFromWorker(filter, forceRefresh) {
    const model = this.facility.getDefaultModel();
    return model.asyncPropertyOperation({
      "operation": 'GET_LAST_READINGS',
      filter,
      ...(forceRefresh && { forceRefresh })
    });
  }

  async refreshStreamsLastReadings() {
    const model = this.facility.getDefaultModel();
    return model.asyncPropertyOperation({ "operation": 'GET_LAST_READINGS', forceRefresh: true });
  }

  _makeIngestionUrl(rawStreamUrl, secret) {
    const url = new URL(this.facility.loadContext.endpoint);
    url.password = secret;
    url.pathname += rawStreamUrl;

    return url.href;
  }

  // returns an ingestion url for the given stream that allows user to push data without supplying forge JWT token
  async getStreamSecret(key) {
    const defaultModel = await this._getOrCreateDefaultModel();
    const res = await fetchJson(this.facility.loadContext, `/models/${defaultModel.urn()}/getstreamssecrets`, "POST", { keys: [key] });

    return res[key];
  }

  // unless hardReset is true, the reset will not replace existing secrets (so it can be used only to backfill empty ones)
  async resetStreamSecrets(keys, hardReset = false) {
    const defaultModel = this.facility.getDefaultModel();
    return fetchJson(this.facility.loadContext, `/models/${defaultModel.urn()}/resetstreamssecrets`, "POST", {
      keys,
      hardReset
    });
  }

  // returns an ingestion url for the given stream that allows user to push data without supplying forge JWT token
  async getStreamIngestionUrl(stream) {
    const secret = await this.getStreamSecret(stream.fullId);
    return this._makeIngestionUrl(stream.streamUrlRaw, secret);
  }

  getWebhookUrl(type = "generic") {
    const defaultModel = this.facility.getDefaultModel();
    return this._makeIngestionUrl(`/timeseries/models/${defaultModel.urn()}/webhooks/${type}`, "");
  }

  formatStreamValue(value, attribute) {
    // precision of 0 means default of 2 digits for connected attributes.
    const precision = attribute.precision || 2;
    if (attribute.forgeUnit) {
      return formatValueWithUnits(
        value,
        "autodesk.unit.unit:" + attribute.forgeUnit,
        3,
        precision,
        { symbolKey: attribute.forgeSymbol }
      );
    }
    if (attribute.dataType === AttributeType.String) {
      return attribute.allowedValues.list[value];
    }
    // Also format the value as a string to ensure that the precision property is respected.
    return formatNumber(value, precision);
  }

  isStreamCandidate(attr) {
    if (attr.dataType === AttributeType.Integer ||
    attr.dataType === AttributeType.Float ||
    attr.dataType === AttributeType.Double) {
      return true;
    } else if (attr.dataType === AttributeType.String) {
      return attr.allowedValues?.list?.length > 0;
    }
    return false;
  }

  /**
   * Find attributes that can be used as "connected" for the given stream
   * @param {Object} stream stream object
   * @param {Object[]} [attrs] default model attrs, more efficient if possible in given context
   */
  async getAttrCandidates(stream, attrs) {
    const defaultModel = this.facility.getDefaultModel();
    if (!attrs) {
      attrs = await defaultModel.getAttributes({ native: true });
    }
    const props = await defaultModel.getPropertiesDt([stream.dbId]);
    const elementPropIds = new Set(Object.values(props[0].element.properties).map(({ uuid }) => uuid));
    const attrCandidates = attrs.filter((attr) => {
      return elementPropIds.has(attr.uuid) && this.isStreamCandidate(attr);
    });

    return attrCandidates;
  }

  getAllConnectedAttributesFromCache() {
    return Array.from(this.connectedAttrsCache.values());
  }

  async getAllConnectedAttributes() {
    const defaultModel = this.facility.getDefaultModel();
    if (!defaultModel) {
      return [];
    }

    await defaultModel.waitForLoad(false, true);

    // ensure cache is populated
    await this.getAllStreamInfos();
    return this.getAllConnectedAttributesFromCache();
  }

  isConnectedAttribute(attrId) {
    return this.connectedAttrsCache.has(attrId);
  }

  async getAllStreamInfos(clearCache = false, dbIds) {
    const streamIds = dbIds ?? (await this.getStreamIds());
    const hadCache = !!this.streamsCache.size;

    if (clearCache) {
      this.streamsCache.clear();
      this.connectedAttrsCache.clear();
      this.inflightCaches.infos.clear();
    }

    // All streams were removed
    if (streamIds.length === 0 && clearCache && hadCache) {
      this.facility.eventTarget.dispatchEvent({ type: dte.DT_STREAMS_INFO_CHANGED_EVENT, dbIds: [] });
      return [];
    }

    const missingFromCache = streamIds.filter((streamId) => !this.streamsCache.has(streamId));

    if (missingFromCache.length > 0) {
      await deduplicatedFetch(missingFromCache, this._fetchAllStreamInfoAndUpdateCache.bind(this), this.inflightCaches.infos);
    }

    return streamIds.
    map((streamId) => this.streamsCache.get(streamId))
    // Filter out undefined. Can happen when streamIds and cache are not in sync anymore due to other parallel events
    .filter(Boolean);
  }

  async _fetchAllStreamInfoAndUpdateCache(streamIDs = []) {
    const defaultModel = this.facility.getDefaultModel();

    if (!defaultModel) {
      return [];
    }

    const dtPkg = defaultModel.getData();
    const parentXrefs = streamIDs.map((id) => {
      const xParentId = dtPkg.dbId2xparentId[id];

      if (Array.isArray(xParentId)) {
        console.warn(`Stream ${id} has ${xParentId.length} hosts - expected 1`);
        return dtPkg.xrefId2fullId[xParentId[0]];
      }

      return dtPkg.xrefId2fullId[xParentId];
    });

    const [fullIds, resolvedParentXRefs, props] = await Promise.all([
    defaultModel.getElementIdsFromDbIds(streamIDs),
    this.facility.resolveXrefs(parentXrefs),
    defaultModel.query({
      dbIds: streamIDs,
      includes: { standard: true, applied: true, element: true, type: false }
    })]
    );

    let updatedStreams = [];
    for (let idx = 0; idx < streamIDs.length; idx++) {
      const dbId = streamIDs[idx];
      const fullId = fullIds[idx];

      const row = props.rows[idx];

      const name = row[QC.Name];
      const dtClass = row[QC.OUniformatClass] || row[QC.UniformatClass];
      const userClassification = row[QC.OClassification] || row[QC.Classification];
      const tandemCategory = row[QC.TandemCategory];
      // Use per-element thresholds
      let settings = row[QC.Settings];

      // to fetch raw data of the stream
      const streamUrlRaw = `/timeseries/models/${defaultModel.urn()}/streams/${fullId}`;

      const candidates = await this.getAttrCandidates({ dbId }, props.cols);
      const streamAttrs = candidates
      // the attribute must be either explicitly flagged as stream, or has calculation settings
      .filter((attr) => (attr.flags & DtConstants.AttributeFlags.afStream) !== 0 || settings?.calculationSettings?.[attr.id]).
      map((attr) => {
        const _attr = structuredClone(attr);
        if (_attr.precision === 0) {
          // backend will fall back to "2" when precision is not set explicitly
          // so we do the same here to make sure properties values formatting works as expected
          _attr.precision = 2;
        }

        // Remove legacy thresholds
        _attr.allowedValues ??= {};
        _attr.allowedValues.thresholds = null;
        return _attr;
      });

      const parent = resolvedParentXRefs[idx];
      if (parentXrefs[idx] && !parent) {
        console.warn(`Failed to resolve parent xref for stream "${name}" (${fullId})`);
      }

      if (settings?.thresholds) {
        // Overwrite all attribute thresholds if the thresholds exist in the settings row
        for (const attr of streamAttrs) {
          if (!attr) continue;

          if (!settings.thresholds[attr.id]?.name) {
            attr.allowedValues.thresholds = null;
            continue;
          }

          // Set default alert settings if they are missing
          settings.thresholds[attr.id].alertDefinition ??= { evaluationPeriodSec: DtConstants.StreamAlertEvaluationPeriod };
          attr.allowedValues.thresholds = settings.thresholds[attr.id];
        }
      }

      // Ensure offlineTimeout is set for all streams
      if (settings?.offlineTimeout === undefined) {
        if (!settings) {
          settings = {};
        }

        settings.offlineTimeout = StreamOfflineTimeouts.Default;
      }

      const info = {
        dbId,
        name,
        dtClass,
        userClassification,
        tandemCategory,
        streamUrlRaw,
        fullStreamUrlRaw: this.facility.loadContext.endpoint + streamUrlRaw,
        fullId,
        hostElement: parent ? { model: parent.model, hostId: parent.dbId } : undefined,
        streamAttrs,
        settings
      };

      if (!this.streamsCache.has(dbId)) {
        this.streamsCache.set(dbId, info);
        updatedStreams.push(dbId);
      }

      for (const attr of streamAttrs) {
        this.connectedAttrsCache.set(attr.id, attr);
      }
    }

    if (updatedStreams.length > 0)
    this.facility.eventTarget.dispatchEvent({ type: dte.DT_STREAMS_INFO_CHANGED_EVENT, dbIds: updatedStreams });
  }

  getAllStreamInfosFromCache() {
    return [...this.streamsCache.values()];
  }

  addIotEventListener() {
    const model = this.facility.getDefaultModel();
    if (!model) return;

    const worker = this.facility.getWorker(model.id);
    this.iotWorkerCbId = worker.registerWorkerCallback(
      (details) => this.onIotNotification(details),
      (details) => console.error('TsWorker failure while processing IOT event', details),
      () => console.warn('unimplemented cb being called')
    );
  }

  removeIotEventListener() {
    const model = this.facility.getDefaultModel();
    if (!model || this.iotWorkerCbId === -1) return;

    const worker = this.facility.getWorker(model.id);
    worker.unregisterWorkerCallback(this.iotWorkerCbId);
  }

  /**
   * Receives IOT events (other than DT_STREAMS_LAST_READINGS_CHANGED_EVENT) from the TsWorker.
   * @param {string} type
   * @param {any} change
   */
  onIotNotification({ type, change }) {
    switch (type) {
      case dte.DT_TELEMETRY_INSTANT_READINGS_CHANGED_EVENT:
        return this.view.handleInstantReadingsChanged(change);
      case dte.DT_TELEMETRY_REGIONS_EVENT:
        return this.view.handleOocRegionsChanged(change);
      // future: we can have an event about arbitrary data changes for "bulk data" and the likes
      default:
        console.info('received unknown IOT event type', type);
        return;
    }
  }

  onModelChanged(e) {
    // Ignore stream data changes, as it doesn't affect the stream info
    if (e.change.ctype === ChangeTypes.DeleteStreamData) {
      return;
    }

    // Ignore events for some model which will be reloaded
    if (e.change.needModelContentsReload) {
      return;
    }

    this.getAllStreamInfos(true);
  }

  async onWsReconnect() {
    // As soon as we disconnected, we could have been missing IOT update events. This will force the next access
    // to re-request the latest readings. We don't have a fallback to REST API while WS is down,
    // since it would add complexity and probably also be down.
    await this.defaultModel?.asyncPropertyOperation({ operation: "MARK_IOT_STALE" });
    return this.refreshStreamsLastReadingsAndAlerts();
  }

  /**
   * Deletes streams incl. timeseries data
   * @param {string[]} streamDbIds
   * @param {number} chunkSize batch size for deleting streams, avoids overloading the server in a single call
   */
  async deleteStreams(streamDbIds, chunkSize = 50) {
    if (!streamDbIds?.length) {
      console.warn('stream ids are missing');
      return;
    }

    let defaultModel = this.facility.getDefaultModel();

    // delete stream elements
    await defaultModel.deleteElements(streamDbIds, DtConstants.ChangeDesc.DeleteStreams);
  }

  async getStreamIds() {
    const defaultModel = await this._getDefaultModel();

    if (!defaultModel) {
      return [];
    }

    let streamIds = [];
    const dbId2flags = defaultModel.getData().dbId2flags;
    for (let i = 0; i < dbId2flags.length; ++i) {
      if (dbId2flags[i] === ElementFlags.Stream) {
        streamIds.push(i);
      }
    }

    return streamIds;
  }

  // Goal is:
  //   - Optionally take stream, streams, or nothing (all).
  //   - Remove need to pre-ask for formatting or other processing, let them do it with the returned object.
  //
  // Overall design
  //   Automatically get instant readings + last readings, in the same object.
  //   Offer a client side hook able to keep this object updated dynamically (react style).

  /**
   * IOT readings for an instant in time.
   * @param {number[]} [dbIds] Optional list of dbIds to restrict the data to.
   * @return {Promise<IotReadings>}
   */
  async getReadings(dbIds) {
    const last = await this.getLastReadingsFromWorker({ dbIds });
    const instant = await this.view.getInstantReadings({ dbIds });

    return new IotReadings(instant, last, this, dbIds);
  }

  /**
   * Store path and timestamp mapping for stream readings
   * Overrules mappings stored at the parameter level in facility template
   * "path" and "ts" are specified in JSONPath without the leading '$.'.
   *
   * @param {number} dbId
   * @param {undefined | {[attrID: string]: {path: string, ts: string, isShared: boolean} }} [mapping] overwrites previous mappings. Example: { "z:CQ": { path: "sensor.data.val", ts: "sensor.ts", isShared: false } }
   */
  async setSourceMappingOverwrite(dbId, mapping) {
    const streams = await this.getAllStreamInfos();
    const stream = streams.find((s) => s.dbId === dbId);

    if (!stream) {
      throw new Error(`Failed to find stream: ${dbId}`);
    }

    const attrs = await this.getAttrCandidates(stream);

    Object.entries(mapping ?? {}).forEach(([attrId, val]) => {
      if (!attrs.some((a) => a.id === attrId)) {
        throw new Error(`Attribute ${attrId} is not applied to the stream`);
      }
      if (val?.path && typeof val.path !== 'string') {
        throw new Error(`Attribute "${attrId}" has an invalid value path`);
      }
      if (val?.ts && typeof val.ts !== 'string') {
        throw new Error(`Attribute "${attrId}" has an invalid timestamp path`);
      }
    });

    const settings = stream.settings ?? {};
    let updated;

    if ((!mapping || !Object.keys(mapping).length) && settings.sourceMapping) {
      // Clear existing mappings
      const { sourceMapping, ...other } = settings;
      updated = other;
    } else if (Object.keys(mapping).length) {
      // Overwrite existing mappings
      const newMappings = {};
      attrs.forEach((attr) => {
        if (mapping[attr.id]) {
          newMappings[attr.id] = {
            path: mapping[attr.id].path,
            ts: mapping[attr.id].ts,
            isShared: mapping[attr.id].isShared
          };
        }
      });
      updated = { ...settings, sourceMapping: newMappings };
    }

    if (updated) {
      await this.#updateStreamsSettingsBulk([dbId], [updated], DtConstants.ChangeDesc.UpdateStreamsSrcMappingOverrides);
    }
  }

  /**
   * Set the time period after which connections are considered offline. In seconds, 0 = never.
   * @param {[number, number][]} timeouts - array of [dbId, timeoutInSeconds] pairs
   */
  async setOfflineTimeout(timeouts) {
    const streams = await this.getAllStreamInfos();
    const streamsMap = new Map();
    for (const stream of streams) {
      streamsMap.set(stream.dbId, stream);
    }

    const dbIds = [];
    const settings = [];
    for (const [dbId, offlineTimeout] of timeouts) {
      const stream = streamsMap.get(dbId);

      if (!stream) {
        throw new Error(`Failed to find stream: ${dbId}}`);
      }

      if (typeof offlineTimeout !== 'number') {
        throw new Error(`offline timeout must be a numeric value`);
      }

      if (stream.settings.offlineTimeout !== offlineTimeout) {
        dbIds.push(dbId);
        settings.push({ ...(stream.settings || {}), offlineTimeout });
      }
    }

    await this.#updateStreamsSettingsBulk(dbIds, settings, DtConstants.ChangeDesc.UpdateStreamOfflineTimeouts);
  }

  async updateStreamDataPointUsage(settingsByDbId) {
    const streams = await this.getAllStreamInfos();
    const streamsMap = new Map();
    for (const stream of streams) {
      streamsMap.set(stream.dbId, stream);
    }

    const dbIds = [];
    const settings = [];
    for (const dbId in settingsByDbId) {
      const stream = streamsMap.get(parseInt(dbId));
      const newSettings = settingsByDbId[dbId];

      if (!stream) {
        throw new Error(`Failed to find stream: ${dbId}}`);
      }

      if (typeof newSettings.frequency !== 'number') {
        throw new Error(`frequency must be a numeric value`);
      }

      if (typeof newSettings.retentionPeriod !== 'number') {
        throw new Error(`retentionPeriod must be a numeric value`);
      }

      if (stream.settings.frequency !== newSettings.frequency || stream.settings.retentionPeriod !== newSettings.retentionPeriod) {
        dbIds.push(dbId);
        settings.push({ ...(stream.settings || {}), frequency: newSettings.frequency, retentionPeriod: newSettings.retentionPeriod });
      }
    }

    await this.#updateStreamsSettingsBulk(dbIds, settings, DtConstants.ChangeDesc.UpdateStreamsDPUsage, true);
  }

  /**
   * Suggests offline timeouts for streams that received data in the given range.
   *
   * @param {DateRange} range interval of date to be considered
   * @param {int[]} thresholds sorted array of time spans in seconds
   * @returns {Object.<string, int>} dictionary of stream id to threshold
   */
  async suggestOfflineTimeouts(range, thresholds = [
  5 * 60, //  5 minutes
  10 * 60, // 10
  15 * 60, // 15
  60 * 60, // 60
  12 * 60 * 60, // 12 hours
  24 * 60 * 60 // 24 hours
  ]) {
    const result = {};

    const readings = await this.readingsByDate.getReadings(range, GRANULARITY.Raw);
    const byStream = Object.groupBy(readings, (r) => r.k);
    for (let streamId in byStream) {
      let maxGap = 0;
      const bySubstream = Object.groupBy(byStream[streamId], (s) => s.s);
      for (let substreamId in bySubstream) {
        const times = bySubstream[substreamId].flatMap((s) => s.t);
        for (let i = 1; i < times.length; ++i) {
          let d = times[i] - times[i - 1];
          if (d > maxGap) {
            maxGap = d;
          }
        }
      }
      if (maxGap > 0) {
        result[streamId] = thresholds.find((t) => t >= maxGap) ?? 0;
      }
    }
    return result;
  }

  async getCurrentAlerts() {
    const defaultModel = this.facility.getDefaultModel();
    const alertInfos = await fetchJson(this.facility.loadContext, `/models/${defaultModel.urn()}/stream-alerts`);

    if (alertInfos) {
      for (const fullId in alertInfos) {
        for (const attrId in alertInfos[fullId]) {
          // Use the notified state, cached alert may be transitory
          const alert = alertInfos[fullId][attrId];
          // @deprecated this is no longer needed with the newest server API.
          alert.displayState = alert.notificationTimestamp < alert.eventTimestamp ?
          alert.previousState :
          alert.currentState;
        }
      }
    }

    return alertInfos;
  }
}